import React from "react";
import { Text } from "@chakra-ui/react";
import { Link } from "react-router-dom";

const CONTACT_EMAIL = "mdeniz.info@gmail.com";
const EFFECTIVE_DATE = "October 22, 2026";
const APP_NAME = "Color Analysis AI & Palette";

function PrivacyPolicy() {
  const App = () => <span style={{ fontWeight: "bold" }}>{APP_NAME}</span>;

  const H = ({ children, id }) => (
    <Text fontSize="l" color="#fff" fontWeight="bold" mt={10} mb={5} id={id}>
      {children}
    </Text>
  );
  const SH = ({ children }) => (
    <Text fontSize="m" color="#fff" fontWeight="bold" mt={6} mb={3}>
      {children}
    </Text>
  );
  const P = ({ children }) => (
    <Text fontSize="m" color="#fff" mt={5} mb={5}>
      {children}
    </Text>
  );
  const Li = ({ children }) => (
    <Text fontSize="m" color="#fff" mt={3} mb={3} ml={4}>
      • {children}
    </Text>
  );
  const Ext = ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: "underline", color: "#fff" }}
    >
      {children}
    </a>
  );

  return (
    <div className="container">
      <div className="header">
        <div className="header__right">
          <a href="/">
            <Text fontWeight="black" color="#fff" fontSize={20}>
              Muhammed Deniz
            </Text>
          </a>
        </div>

        <div className="header__left">
          <Link to="/home">
            <Text fontSize="m" mr={5} fontWeight="black" color="#fff">
              Home
            </Text>
          </Link>

          <Link to="/aicoloranalysis/privacypolicy">
            <Text fontSize="m" mr={5} fontWeight="black" color="#fff">
              Privacy Policy
            </Text>
          </Link>

          <Link to="/aicoloranalysis/termsofuse">
            <Text fontSize="m" mr={5} fontWeight="black" color="#fff">
              Terms Of Use
            </Text>
          </Link>
        </div>
      </div>

      <div style={{ margin: 60, marginLeft: "10%", marginRight: "15%" }}>
        <Text fontSize="xl" mb={5} fontWeight="black" color="#fff">
          Privacy Policy
        </Text>

        <P>
          Your privacy is important to us. It is Muhammed Deniz's policy to
          respect your privacy regarding any information we may collect from you
          through our app, <App />.
        </P>

        <P>
          We only ask for personal information when we truly need it to provide
          a service to you. We collect it by fair and lawful means, with your
          knowledge and consent. We also let you know why we’re collecting it
          and how it will be used.
        </P>

        <P>
          We only retain collected information for as long as necessary to
          provide you with your requested service. What data we store, we’ll
          protect within commercially acceptable means to prevent loss and
          theft, as well as unauthorised access, disclosure, copying, use or
          modification.
        </P>

        <P>
          We don’t sell your personal information, and we don’t share any
          personally identifying information publicly or with third parties,
          except as described in this policy (for example, the AI and service
          providers that make the app work) or when required to by law.
        </P>

        <P>
          Our app may link to external sites that are not operated by us. Please
          be aware that we have no control over the content and practices of
          these sites, and cannot accept responsibility or liability for their
          respective privacy policies.
        </P>

        <P>
          Your continued use of our app will be regarded as acceptance of our
          practices around privacy and personal information. If you have any
          questions about how we handle user data and personal information, feel
          free to contact us at{" "}
          <Ext href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Ext>.
        </P>

        <P>This policy is effective as of {EFFECTIVE_DATE}.</P>

        {/* ------------------------------------------------------------ */}
        <H id="information-we-collect">1. Information We Collect and How</H>

        <P>
          <App /> analyzes the colors and style in a photo you choose: a photo
          of your face for a personal color analysis (for example your undertone
          and which colors suit you), or a photo of an outfit for an outfit and
          color analysis. It does not require you to create an account, and we
          do not ask for your name, email address, phone number, contacts or
          location. The information below is what the app and our service
          providers handle.
        </P>

        <SH>Photos you choose to submit</SH>
        <Li>
          The photo you select or take (which may show your face or body) is
          collected only when you start an analysis.
        </Li>
        <Li>
          Before the first time a photo is sent to a third-party AI service, the
          app asks for your explicit permission (see “Consent and How to
          Withdraw It”).
        </Li>
        <Li>
          The app only accesses the photos you choose through the system photo
          picker or camera. It does not browse your photo library.
        </Li>

        <SH>Anonymous app identifier and purchase information</SH>
        <Li>
          A random, anonymous app user ID created by RevenueCat, our
          subscription provider. It is used to check your subscription status.
          It is not linked to your name or email.
        </Li>
        <Li>
          Purchase and subscription information (product, status, dates)
          processed by Apple App Store / Google Play and RevenueCat. We never
          receive your payment card details.
        </Li>

        <SH>Usage and diagnostics</SH>
        <Li>
          Google Firebase Analytics may collect app interactions, device and OS
          information, and device or app-instance identifiers, to help us
          understand how the app is used and fix problems.
        </Li>
        <Li>
          Google Firebase Crashlytics may collect crash reports and diagnostic
          information (for example device model, OS version, app version and the
          state of the app when it crashed) so that we can find and fix crashes.
        </Li>
        <Li>
          If you allow the tracking permission prompt shown by the app, your
          device’s advertising identifier may also be available to our analytics
          provider for app analytics and for measuring the app’s performance
          (see “Tracking and Advertising”).
        </Li>

        <SH>Usage limits, reports and support</SH>
        <Li>
          To prevent abuse and keep the service available, our servers keep
          short-term technical records such as request counts together with the
          IP address or anonymous app identifier of the request, used to apply
          usage limits (rate limiting). These records do not contain your photo.
        </Li>
        <Li>
          When you use the app’s Report button, we receive the reason you
          choose, the details you enter, the app name, the app version and your
          platform (iOS or Android). The report does not include your photo.
        </Li>
        <Li>
          If you email us, we receive your email address and what you write.
        </Li>

        <SH>Data kept only on your device</SH>
        <Li>
          Apart from sending your photo to our service to create a result, your
          photos, analysis results and history are stored locally on your
          device. Results contain the color and style details the analysis
          produced from your photo (for example undertone, face shape, skin, eye
          and hair color, and suggested colors). They are removed when you
          delete the app.
        </Li>

        {/* ------------------------------------------------------------ */}
        <H id="how-we-use">2. How We Use Information</H>
        <Li>To create and deliver the analysis you request.</Li>
        <Li>To verify your subscription and provide paid features.</Li>
        <Li>To prevent abuse and apply usage limits.</Li>
        <Li>To maintain, secure and improve the app and fix crashes.</Li>
        <Li>To respond to your reports and support requests.</Li>
        <P>
          We do not use your photos for advertising, marketing, profiling,
          identification, authentication, or data mining, and we do not allow
          our providers to do so on our behalf. Data collected for one purpose
          is not reused for a different purpose without your further consent,
          unless the law permits it.
        </P>

        {/* ------------------------------------------------------------ */}
        <H id="third-party-ai">3. Third-Party AI and Service Providers</H>

        <P>
          The photo you choose to submit is sent over an encrypted connection
          through our server to the following third-party service. We only do
          this after you give permission in the app.
        </P>

        <SH>AI analysis</SH>
        <Li>
          OpenAI, L.L.C. analyzes your photo to create your color or outfit
          analysis. OpenAI receives your photo only for this purpose and applies
          its own safety measures under its policies.
        </Li>
        <Li>
          Privacy policy:{" "}
          <Ext href="https://openai.com/policies/privacy-policy">
            https://openai.com/policies/privacy-policy
          </Ext>
        </Li>

        <SH>Other service providers</SH>
        <Li>
          Supabase hosts the server components that receive your request and
          pass it to OpenAI. It is used only for security purposes (abuse
          prevention and API key protection).
        </Li>
        <Li>
          RevenueCat processes subscription status:{" "}
          <Ext href="https://www.revenuecat.com/privacy/">
            https://www.revenuecat.com/privacy/
          </Ext>
        </Li>
        <Li>
          Google Firebase (Analytics and Crashlytics):{" "}
          <Ext href="https://firebase.google.com/support/privacy">
            https://firebase.google.com/support/privacy
          </Ext>
        </Li>
        <Li>
          Apple (App Store) and Google (Google Play) process payments and
          subscriptions under their own terms.
        </Li>

        <P>
          We share data only with providers needed to operate the app, and we
          select providers that give the same or an equal level of protection of
          user data as described in this policy and required by the App Store
          Review Guidelines. These providers process your data under their own
          privacy policies, including how long they retain it, so please review
          them. We do not sell, rent or share your photos with any other third
          parties.
        </P>

        {/* ------------------------------------------------------------ */}
        <H id="face-data-processing">4. Photos and Face Images</H>

        <P>
          Some photos you choose may show your face. Here is how we handle them:
        </P>

        <Li>
          The photo is used only to create the analysis you asked for. It is
          processed when you request an analysis.
        </Li>
        <Li>
          We do not create or store biometric identifiers or face templates
          (such as facial recognition data), and we do not use face mapping or
          depth data. The analysis only describes colors and general features
          (such as undertone or face shape) to give style suggestions.
        </Li>
        <Li>
          Photos and results are not used for identification, authentication,
          advertising, marketing, profiling or data mining, by us or by third
          parties.
        </Li>
        <Li>
          Our servers pass your photo to OpenAI and do not save a copy of it.
          How long OpenAI keeps data is decided by OpenAI under its own policy,
          which can change, so please check it (linked in section 3).
        </Li>
        <Li>
          All data transmission is encrypted using industry-standard protocols,
          and we apply reasonable technical and organizational measures to
          protect your data.
        </Li>

        {/* ------------------------------------------------------------ */}
        <H id="consent">5. Consent and How to Withdraw It</H>
        <Li>
          Before your photo is sent to a third-party AI service, the app clearly
          shows which service receives the data and asks for your explicit
          permission. The app does not send it without that permission.
        </Li>
        <Li>
          You can withdraw this permission at any time in the app under Settings
          → Withdraw AI Data Consent. After that, the app asks again before
          sending your next photo. You can also simply stop using the analysis
          features or delete the app.
        </Li>
        <Li>
          You choose which photo to submit, and you can choose not to upload any
          facial images at any time. Processing happens only when you start an
          analysis.
        </Li>
        <Li>
          You can revoke photo and camera access at any time in your device
          settings.
        </Li>
        <Li>
          Paid features do not depend on you agreeing to analytics or tracking.
        </Li>

        {/* ------------------------------------------------------------ */}
        <H id="retention">6. Data Retention and Deletion</H>
        <Li>
          We do not keep copies of your photos or analysis results on our
          servers. Your photos, results and history stay on your device until
          you delete the app, which removes them.
        </Li>
        <Li>
          Short-term technical records used for usage limits (such as request
          counts and the related IP address or anonymous identifier) are kept
          only briefly and are removed automatically.
        </Li>
        <Li>
          Reports you send us are kept only for a limited period and are deleted
          regularly. We may keep them longer only where needed for a legal
          obligation.
        </Li>
        <Li>
          Analytics, crash and purchase data are kept by Firebase, RevenueCat,
          Apple and Google under their retention policies.
        </Li>
        <P>
          We do not keep photos, analysis results or user accounts on our
          servers, so there is no such data for us to delete, and the short-term
          technical records described above are removed automatically. Your
          local data is removed when you delete the app. If you have questions
          about your data, contact us at{" "}
          <Ext href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Ext>.
        </P>

        {/* ------------------------------------------------------------ */}
        <H id="safety">7. Safety and Reporting</H>
        <Li>
          We do not run a separate automated scan of photos. We rely on the
          built-in safety measures of our AI provider, which may decline to
          analyze unsafe content.
        </Li>
        <Li>
          You can report any result inside the app with the Report button, or in
          Settings, or by emailing{" "}
          <Ext href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Ext>. We review
          reports and take appropriate action where needed.
        </Li>
        <Li>
          Where required by law, we may disclose information to the relevant
          authorities, for example about illegal content.
        </Li>

        {/* ------------------------------------------------------------ */}
        <H id="children">8. Children</H>
        <P>
          The photo analysis features of <App /> are intended for users aged 18
          and over. We do not knowingly collect personal information from anyone
          under 18, and these features must not be used with photos of anyone
          under 18. If you believe a minor’s information has been submitted,
          contact us.
        </P>

        {/* ------------------------------------------------------------ */}
        <H id="tracking">9. Tracking and Advertising</H>
        <Li>
          The app may ask for your permission to use your device’s advertising
          identifier (the App Tracking Transparency prompt on iOS, and the
          advertising ID permission on Android).
        </Li>
        <Li>
          If you allow it, the identifier is available to our analytics provider
          (Google Firebase) and is used for app analytics and for measuring the
          app’s performance.
        </Li>
        <Li>
          We do not show advertising in the app and we do not sell your personal
          information. Your photos are not used for advertising or marketing.
        </Li>
        <Li>
          If you decline, the app works the same. You can change your choice at
          any time: on iOS in Settings → Privacy &amp; Security → Tracking, and
          on Android in Settings → Privacy → Ads.
        </Li>

        {/* ------------------------------------------------------------ */}
        <H id="your-rights">10. Your Rights and Choices</H>
        <P>
          Depending on where you live (for example in the EU/EEA, UK or
          California), you may have the right to access, correct, delete or
          restrict the use of your personal data, to object to processing, to
          withdraw consent, and to complain to your local data protection
          authority. Where we rely on your consent (such as sending photos to an
          AI service), you can withdraw it at any time as described above. Since
          we do not hold personal data that identifies you, most of these rights
          do not apply to data held by us. For data held by our providers (such
          as RevenueCat or Firebase), you can contact us or the provider. To
          exercise a right, contact us at{" "}
          <Ext href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Ext>.
        </P>

        <H id="transfers">11. International Data Transfers</H>
        <P>
          Our providers may process data in countries other than your own,
          including the United States. By giving your permission in the app, you
          agree to this transfer, which is subject to the safeguards and
          policies of those providers.
        </P>

        <H id="changes">12. Changes to This Policy</H>
        <P>
          We may update this policy from time to time. The latest version is
          always available at this page, with the effective date shown above. If
          we make material changes to how your data is used, we will ask for
          your permission again in the app where required.
        </P>

        <H id="contact">13. Contact</H>
        <P>
          Questions, requests or reports:{" "}
          <Ext href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Ext>
        </P>

        <H id="terms">14. Terms of Use</H>
        <P>
          Your use of <App /> is also subject to our{" "}
          <Link
            to="/aicoloranalysis/termsofuse"
            style={{ textDecoration: "underline", color: "#fff" }}
          >
            Terms of Use
          </Link>
          , which cover the license, subscriptions, acceptable use, disclaimers
          and governing law.
        </P>

        <Text fontSize="s" color="#fff" mt={10} textAlign="right">
          Last updated: {EFFECTIVE_DATE}
        </Text>

        <Text
          fontSize="xl"
          color="#fff"
          fontWeight="hairline"
          mt={20}
          mb={0}
          align="right"
        >
          Muhammed Deniz
        </Text>

        <Text
          fontSize="s"
          color="#fff"
          fontWeight="hairline"
          mt={0}
          mb={2}
          align="right"
        >
          <App />
        </Text>

        <Text fontSize="s" color="#fff" mb={20} align="right">
          Contact: <Ext href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Ext>
        </Text>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
